import type { ReactNode } from 'react';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCompany } from '../../state/features/company/slice';
import { setLocations } from '../../state/features/locations/slice';
import CompanyErrorScreen from './errorScreen';
import CompanyLoadingScreen from './loadingScreen';
import { useDataWarehouseConfigurations } from '../../helpers/hooks/useDataWarehouseConfigurations';
import { useFetchCompanyData } from '../../helpers/hooks/useFetchCompanyData';
import { useFetchLocationsData } from '../../helpers/hooks/useFetchLocationsData';
import { useSimulateLoader } from '../../helpers/hooks/useSimulateLoader';
import DashboardMarketingContentView from './dashboardMarketingContentView';
import { ENTITY_LOCK_TYPE } from '../../types/visibility';
import AuthTokenError from '../authTokenError';
import { useFetchDashboardList } from '../../helpers/hooks/useFetchDashboardsList';
import { setDashboardsList } from '../../state/features/dashboard/slice';
import { addToSentryContext, setSentryTags } from '../../services/sentry/utils';
import { DashboardEventsContext } from '../dashboardEvents';
import type { RootState } from '../../state/store';
import DashboardDefaults from '../dashboardDefaults';
import { filterLocationsBySelectedDateRange, saveDecimalPrecision } from '../../helpers/utils';

const loadingStates: Record<number, string> = {
  1: 'Connecting to Data Warehouse...',
  2: 'Loading Company Profile...',
  3: 'Fetching locations...',
  4: 'Loading dashboard...',
  5: 'Initializing...'
};

const loadErrorStates: Record<number, string> = {
  1: 'Failed to connect to Data Warehouse. Please try again',
  2: 'Failed to fetch company profile. Please try again',
  3: 'Failed to fetch locations data. Please try again',
  4: 'Failed to fetch dashboards data. Please try again or contact your administrator'
};

type CompanyProviderProps = {
  children: ReactNode;
};

type CompanyData = {
  company_id: number;
  currency_precision?: number;
  authorized_location_ids?: number[];
  user_id?: number;
  user_name?: string;
  company_name?: string;
  country?: string;
  time_zone?: string;
  business_end_time?: number;
};

type Location = {
  id: number;
};

const CompanyProvider = ({ children }: CompanyProviderProps) => {
  const authorizedLocationIds = useRef<number[]>([]);
  const lastCompanyId = useRef<number | null>(null);
  const locationsFetchedRef = useRef(false);
  const dashboardsFetchedRef = useRef(false);
  const dispatch = useDispatch();
  const { updateCompanyId } = useContext(DashboardEventsContext);
  const { isAdminUser } = useSelector((state: RootState) => state.company);
  const { dates } = useSelector((state: RootState) => state.filters);
  const datesRef = useRef(dates);

  datesRef.current = dates;

  const configureCompanyInSentry = (data: CompanyData) => {
    try {
      const {
        user_id,
        user_name,
        company_id,
        company_name,
        country,
        time_zone,
        business_end_time
      } = data;
      addToSentryContext({
        name: 'Company Details',
        contexts: {
          user_id,
          user_name,
          company_id,
          company_name,
          country,
          time_zone,
          business_end_time
        }
      });
      setSentryTags({ user_id, user_name, company_id, company_name });
    } catch (error) {
      console.error('Failed to set sentry company contexts', error);
    }
  };

  const { companyData, companyDataLoadError, fetchCompany } = useFetchCompanyData({});

  const { locationData, locationDataLoadError, fetchLocations } = useFetchLocationsData({});

  // Fetch company data immediately on mount so we know enable_clickhouse before
  // deciding which warehouse(s) to warm up. Without this, production CH companies
  // would incorrectly trigger a Databricks warm-up on every page load.
  useEffect(() => {
    fetchCompany();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived from company data — accurate for both mock logins (JWT flag) and real
  // production companies (server-side CompanyMigrator, returned via /api/v1/me).
  const isClickhouseEnabled = Boolean((companyData as any)?.enable_clickhouse);

  const { isDataWarehouseUp, isWarehouseLoadError, vantageLockedStatus } =
    useDataWarehouseConfigurations({
      maxRetries: 4,
      retryInterval: 10,
      isClickhouseEnabled,
      // Only start warehouse checks once we have company data and know isClickhouseEnabled.
      shouldStart: Boolean(companyData),
      onDataWarehouseUp() {
        fetchLocations();
      }
    });

  const { dashboardsListData, dashboardListDataFetchError, fetchDashboardList } =
    useFetchDashboardList({});

  const {
    isLoaded: simulateLoaded,
    isLoading: simulatedLoading,
    reload: simulateLoader
  } = useSimulateLoader({
    duration: 2,
    startLoadingOnMount: false
  });

  useEffect(() => {
    if (!companyData) {
      return;
    }
    if (lastCompanyId.current === companyData.company_id) {
      return;
    }
    lastCompanyId.current = companyData.company_id;
    updateCompanyId(companyData.company_id);
    saveDecimalPrecision(companyData.currency_precision);
    dispatch(setCompany(companyData));
    configureCompanyInSentry(companyData);
    authorizedLocationIds.current = companyData?.authorized_location_ids || [];
    // Locations are fetched via onDataWarehouseUp once the warehouse is confirmed ready.
  }, [companyData, dispatch, updateCompanyId]);

  useEffect(() => {
    if (!locationData) {
      return;
    }
    let accessibleLocations = locationData as Location[];
    if (authorizedLocationIds.current.length > 0 && !isAdminUser) {
      accessibleLocations = (locationData as Location[]).filter((location) =>
        authorizedLocationIds.current.includes(location.id)
      );
    }

    const filteredLocations = filterLocationsBySelectedDateRange(
      accessibleLocations,
      datesRef.current
    );

    dispatch(setLocations(filteredLocations));
    if (!dashboardsFetchedRef.current) {
      dashboardsFetchedRef.current = true;
      fetchDashboardList();
    }
  }, [locationData, isAdminUser, dispatch, fetchDashboardList]);

  useEffect(() => {
    if (!dashboardsListData) {
      return;
    }
    dispatch(setDashboardsList(dashboardsListData));
    if (!simulateLoaded && !simulatedLoading) {
      simulateLoader();
    }
  }, [dashboardsListData, dispatch, simulateLoader, simulateLoaded, simulatedLoading]);

  const progress = useMemo(() => {
    const steps = [
      { ready: isDataWarehouseUp, label: loadingStates[1] },
      { ready: Boolean(companyData), label: loadingStates[2] },
      { ready: Boolean(locationData), label: loadingStates[3] },
      { ready: Boolean(dashboardsListData), label: loadingStates[4] },
      { ready: simulateLoaded, label: loadingStates[5] }
    ];
    const pendingIndex = steps.findIndex((step) => !step.ready);
    const statusText = steps[pendingIndex]?.label ?? loadingStates[5];
    const isLoading = pendingIndex !== -1;
    return {
      isLoading,
      progressCount: ((pendingIndex === -1 ? steps.length : pendingIndex + 1) / 4) * 100,
      statusText
    };
  }, [companyData, dashboardsListData, isDataWarehouseUp, locationData, simulateLoaded]);

  const appLoadError = useMemo(() => {
    const isCompanyLoadError = !companyData && companyDataLoadError;
    const isLocationsLoadError = !locationData && locationDataLoadError;
    const isDashboardsLoadError = !dashboardsListData && dashboardListDataFetchError;
    const errorSequence = [
      isWarehouseLoadError,
      isCompanyLoadError,
      isLocationsLoadError,
      isDashboardsLoadError
    ];
    const errorStateIndex = errorSequence.lastIndexOf(true);
    const statusText = loadErrorStates[errorStateIndex + 1];
    const isError = errorSequence.includes(true);
    return {
      isError,
      statusText
    };
  }, [
    companyData,
    companyDataLoadError,
    dashboardListDataFetchError,
    dashboardsListData,
    isWarehouseLoadError,
    locationData,
    locationDataLoadError
  ]);

  if (vantageLockedStatus.isEnabled) {
    if (vantageLockedStatus.config.lockType === ENTITY_LOCK_TYPE.MARKETING_MESSAGE) {
      return <DashboardMarketingContentView url={vantageLockedStatus?.config?.marketingHtmlUrl} />;
    }
    if (vantageLockedStatus.config.lockType === ENTITY_LOCK_TYPE.UNAUTHORIZED) {
      return <AuthTokenError />;
    }
  }

  if (appLoadError.isError) {
    return <CompanyErrorScreen errorText={appLoadError} />;
  }

  if (progress.isLoading) {
    return <CompanyLoadingScreen statusText={progress.statusText} />;
  }

  return companyData && dashboardsListData ? (
    <>
      <DashboardDefaults companyData={companyData} />
      {locationData && simulateLoaded ? <>{children}</> : null}
    </>
  ) : null;
};

export default CompanyProvider;
